import { useTranslation } from "react-i18next";
import { showSuccess, showApiError } from "@/lib/toast";
import {
  addCampaignDetailContent,
  addCampaignDetailDate,
  addCampaignDetailItem,
  getCampaignDetailItems,
  getCampaignDetails,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { CampaignCalendarItemMetaForm, FormSchemaType } from "./CampaignCalendarItemMetaForm";
import { convertTimeToUtc, formatToUTC } from "@/lib/dateUtils";
import { addDays } from "date-fns";

type Props = {};

export const CampaignCalendarItemAddForm: React.FC<Props> = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [queryParams] = useSearchParams();
  const campaignId = queryParams.get("Id");

  const { data: campaignDetails } = useQuery({
    queryFn: () => getCampaignDetails(String(campaignId), String(activeFirm?.firmGuid)),
    queryKey: ["getCampaignDetails", campaignId],
  });

  const campaignDetailsData = campaignDetails?.data;

  const queryClient = useQueryClient();
  const addEventMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      if (values.periodEnd === values.periodStart) {
        values.periodEnd = addDays(formatToUTC(values.periodEnd), 1).toISOString();
      } else {
        values.periodEnd = formatToUTC(values.periodEnd);
      }

      const campaignDetailItem = await addCampaignDetailItem({
        campaignId: Number(campaignDetailsData?.intId),
        periodStart: values.periodStart,
        periodEnd: values.periodEnd,
        isActive: values.isActive,
        firmId: String(activeFirm?.firmGuid),
      });

      const campaignDetailItems = await getCampaignDetailItems(String(campaignId), String(activeFirm?.firmGuid));

      const campaignDetailItemsData = campaignDetailItems?.data.result || [];
      const newCampaignDetailItem = campaignDetailItemsData.find(
        (item) => item.id === campaignDetailItem.data.newDataId
      );

      for (const content of values.contentList) {
        if (content.contentId && newCampaignDetailItem?.intId) {
          await addCampaignDetailContent({
            campaignDetailId: newCampaignDetailItem.intId,
            contentId: content.contentId || undefined,
            firmGuid: String(activeFirm?.firmGuid),
          });
        }
      }

      for (const day of values.weekDays) {
        await addCampaignDetailDate({
          campaignDetailId: Number(newCampaignDetailItem?.intId),
          firmGuid: String(activeFirm?.firmGuid),
          dayOfWeek: day.dayOfWeek,
          startTime: convertTimeToUtc(day.startTime),
          endTime: convertTimeToUtc(day.endTime),
        });
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailItems"] });
      showSuccess(t("common.addedEventSuccess"));
    },
    onError: showApiError,
  });

  return (
    <CampaignCalendarItemMetaForm
      buttonText={t("common.save")}
      onSubmit={(values) => addEventMutation.mutate(values)}
      isLoading={addEventMutation.isPending}
    />
  );
};
